import { useState, useRef, useCallback, useMemo } from "react";
import {
  ChevronLeft,
  ChevronRight,
  Copy,
  Check,
  Loader2,
  ArrowUp,
  Trash2,
  RotateCcw,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import type { AnimationConfig, AnimationControl } from "@/lib/api";
import { cn } from "@/lib/utils";

// Same gradient pool as AI chat bubbles (purple)
const BUBBLE_GRADIENTS = [
  "linear-gradient(135deg, rgba(125,92,240,0.09) 0%, rgba(110,78,220,0.04) 100%)",
  "linear-gradient(150deg, rgba(130,95,245,0.08) 0%, rgba(115,82,228,0.04) 100%)",
  "linear-gradient(120deg, rgba(120,88,235,0.09) 0%, rgba(128,95,240,0.04) 100%)",
  "linear-gradient(140deg, rgba(128,94,242,0.08) 0%, rgba(105,75,218,0.04) 100%)",
  "linear-gradient(160deg, rgba(135,98,248,0.08) 0%, rgba(118,85,232,0.04) 100%)",
];

function pickGradient(seed: string): string {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) hash = ((hash << 5) - hash + seed.charCodeAt(i)) | 0;
  return BUBBLE_GRADIENTS[Math.abs(hash) % BUBBLE_GRADIENTS.length]!;
}

interface AnimationDesignerProps {
  data: {
    config: AnimationConfig;
    prompt?: string;
  };
  onAction?: (actionId: string, context?: Record<string, unknown>) => void;
  onInputFocus?: (focused: boolean) => void;
}

function isEmptyConfig(config: AnimationConfig): boolean {
  return !config || !config.keyframes;
}

/** Merge override values into properties for preview */
function mergeOverrides(
  config: AnimationConfig,
  overrides: Record<string, number | boolean | string>,
): AnimationConfig {
  const props = { ...config.properties };

  for (const [key, value] of Object.entries(overrides)) {
    switch (key) {
      case "duration": {
        props.duration = `${value}s`;
        break;
      }
      case "delay": {
        props.delay = `${value}s`;
        break;
      }
      case "easing": {
        props.easing = String(value);
        break;
      }
      case "infinite": {
        props.iterationCount = value ? "infinite" : "1";
        break;
      }
      case "direction": {
        props.direction = String(value);
        break;
      }
      default:
        break;
    }
  }

  return { ...config, properties: props };
}

/** Build the CSS animation shorthand string */
function buildAnimationCSS(config: AnimationConfig): string {
  const p = config.properties;
  return `anim ${p.duration} ${p.easing} ${p.delay} ${p.iterationCount} ${p.direction} ${p.fillMode}`;
}

/** Build full CSS string for clipboard */
function buildFullCSS(config: AnimationConfig): string {
  const p = config.properties;
  return `${config.keyframes}

.animated-element {
  animation: anim ${p.duration} ${p.easing} ${p.delay} ${p.iterationCount} ${p.direction} ${p.fillMode};
}`;
}

// ── Demo Elements ──────────────────────────────────────────

function DemoBox({ style, animKey }: { style: React.CSSProperties; animKey: number }) {
  return (
    <div
      key={animKey}
      className="w-20 h-20 rounded-2xl"
      style={{
        background: "linear-gradient(135deg, #e8a44a 0%, #e86f4a 100%)",
        ...style,
      }}
    />
  );
}

function DemoCard({ style, animKey }: { style: React.CSSProperties; animKey: number }) {
  return (
    <div
      key={animKey}
      className="w-48 rounded-xl border border-white/10 overflow-hidden"
      style={{
        background: "rgba(255,255,255,0.06)",
        backdropFilter: "blur(8px)",
        ...style,
      }}
    >
      <div className="p-3.5">
        <div className="h-2.5 w-20 rounded bg-white/30 mb-2" />
        <div className="h-2 w-32 rounded bg-white/15 mb-1.5" />
        <div className="h-2 w-28 rounded bg-white/15" />
      </div>
    </div>
  );
}

function DemoCircle({ style, animKey }: { style: React.CSSProperties; animKey: number }) {
  return (
    <div
      key={animKey}
      className="w-16 h-16 rounded-full"
      style={{
        background: "linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)",
        ...style,
      }}
    />
  );
}

function DemoText({ style, animKey }: { style: React.CSSProperties; animKey: number }) {
  return (
    <span
      key={animKey}
      className="text-lg font-semibold text-white/90 select-none"
      style={style}
    >
      Hello, World
    </span>
  );
}

function DemoMultiple({ style, animKey }: { style: React.CSSProperties; animKey: number }) {
  return (
    <div key={animKey} className="flex gap-3">
      {[0, 1, 2, 3].map((i) => (
        <div
          key={i}
          className="w-10 h-10 rounded-lg"
          style={{
            background: `linear-gradient(135deg, hsl(${200 + i * 30}, 70%, 60%) 0%, hsl(${220 + i * 30}, 70%, 50%) 100%)`,
            animationDelay: `${i * 0.1}s`,
            ...style,
          }}
        />
      ))}
    </div>
  );
}

function DemoCanvas({ style, animKey }: { style: React.CSSProperties; animKey: number }) {
  return (
    <div
      key={animKey}
      className="absolute inset-0"
      style={{
        background: "linear-gradient(135deg, rgba(139,92,246,0.3) 0%, rgba(99,102,241,0.15) 100%)",
        ...style,
      }}
    />
  );
}

function renderDemo(
  demo: string,
  animStyle: React.CSSProperties,
  animKey: number,
) {
  switch (demo) {
    case "card":
      return <DemoCard style={animStyle} animKey={animKey} />;
    case "circle":
      return <DemoCircle style={animStyle} animKey={animKey} />;
    case "text":
      return <DemoText style={animStyle} animKey={animKey} />;
    case "multiple":
      return <DemoMultiple style={animStyle} animKey={animKey} />;
    case "canvas":
      return <DemoCanvas style={animStyle} animKey={animKey} />;
    default:
      return <DemoBox style={animStyle} animKey={animKey} />;
  }
}

// ── Control Renderers ──────────────────────────────────────

function SliderControl({
  control,
  value,
  onChange,
}: {
  control: AnimationControl;
  value: number;
  onChange: (val: number) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-2xs text-daydra-muted w-16 shrink-0 text-right">
        {control.label}
      </label>
      <input
        type="range"
        min={control.min ?? 0}
        max={control.max ?? 3}
        step={control.step ?? 0.1}
        value={value}
        onChange={(e) => onChange(parseFloat(e.target.value))}
        className="flex-1 h-1 appearance-none bg-white/10 rounded-full outline-none cursor-pointer
          [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-3.5 [&::-webkit-slider-thumb]:h-3.5
          [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-white [&::-webkit-slider-thumb]:cursor-pointer
          [&::-webkit-slider-thumb]:shadow-[0_0_8px_rgba(255,255,255,0.3)]"
      />
      <span className="text-2xs text-daydra-dim font-mono w-12 text-right">
        {typeof value === "number" ? value.toFixed(1) : value}
        {control.unit ?? ""}
      </span>
    </div>
  );
}

function ToggleControl({
  control,
  value,
  onChange,
}: {
  control: AnimationControl;
  value: boolean;
  onChange: (val: boolean) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-2xs text-daydra-muted w-16 shrink-0 text-right">
        {control.label}
      </label>
      <button
        onClick={() => onChange(!value)}
        className={cn(
          "relative w-9 h-5 rounded-full transition-colors duration-normal",
          value ? "bg-[#e8a44a]" : "bg-white/15",
        )}
      >
        <span
          className={cn(
            "absolute top-[3px] left-[3px] w-[14px] h-[14px] rounded-full bg-white transition-all duration-normal shadow-sm",
            value && "translate-x-[16px]",
          )}
        />
      </button>
    </div>
  );
}

function SelectControl({
  control,
  value,
  onChange,
}: {
  control: AnimationControl;
  value: string;
  onChange: (val: string) => void;
}) {
  return (
    <div className="flex items-center gap-3">
      <label className="text-2xs text-daydra-muted w-16 shrink-0 text-right">
        {control.label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="flex-1 bg-white/[0.06] border border-white/[0.08] rounded-lg px-2 py-1 text-2xs text-daydra-text
          outline-none cursor-pointer appearance-none"
      >
        {(control.options ?? []).map((opt) => (
          <option key={opt} value={opt} className="bg-[#1e1e22] text-white">
            {opt}
          </option>
        ))}
      </select>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────────

export function AnimationDesigner({ data, onAction, onInputFocus }: AnimationDesignerProps) {
  const [versions, setVersions] = useState<AnimationConfig[]>(
    isEmptyConfig(data.config) ? [] : [data.config],
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [overrides, setOverrides] = useState<Record<string, number | boolean | string>>({});
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [playCount, setPlayCount] = useState(0);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);

  const isEmpty = versions.length === 0;
  const currentConfig = isEmpty ? null : (versions[currentIndex] ?? versions[0]);
  const mergedConfig = currentConfig ? mergeOverrides(currentConfig, overrides) : null;
  const canGoBack = currentIndex > 0;
  const canGoForward = currentIndex < versions.length - 1;

  // Build animation style for the demo element
  const animStyle = useMemo<React.CSSProperties>(() => {
    if (!mergedConfig || !mergedConfig.keyframes) return {};
    return { animation: buildAnimationCSS(mergedConfig) };
  }, [mergedConfig, playCount]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleIterate = useCallback(async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    setLoading(true);
    try {
      if (isEmpty || !currentConfig) {
        const result = await api.animation.generate(prompt);
        if (result.config && !isEmptyConfig(result.config)) {
          setVersions([result.config]);
          setCurrentIndex(0);
          setOverrides({});
          setInput("");
        }
      } else {
        const configToIterate = mergedConfig ?? currentConfig;
        const result = await api.animation.iterate(configToIterate, prompt);
        if (result.config) {
          const newVersions = [...versions.slice(0, currentIndex + 1), result.config];
          setVersions(newVersions);
          setCurrentIndex(newVersions.length - 1);
          setOverrides({});
          setInput("");
        }
      }
    } catch {
      // Silently fail — animation stays as-is
    } finally {
      setLoading(false);
    }
  }, [input, loading, currentConfig, mergedConfig, isEmpty, versions, currentIndex]);

  const handleClear = useCallback(() => {
    setVersions([]);
    setCurrentIndex(0);
    setOverrides({});
    setInput("");
    setPlayCount(0);
  }, []);

  const handleCopy = useCallback(async () => {
    if (!mergedConfig) return;
    try {
      await navigator.clipboard.writeText(buildFullCSS(mergedConfig));
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API may not be available
    }
  }, [mergedConfig]);

  const handleReplay = useCallback(() => {
    setPlayCount((c) => c + 1);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!mergedConfig) return;
    const css = buildFullCSS(mergedConfig);
    onAction?.("animation_confirm", {
      __dismiss: true,
      __action: "animation_confirm",
      __payload: {
        config: mergedConfig,
        prompt: data.prompt ?? "",
        css,
      },
    });
  }, [mergedConfig, data.prompt, onAction]);

  const handleControlChange = useCallback(
    (key: string, value: number | boolean | string) => {
      setOverrides((prev) => ({ ...prev, [key]: value }));
      // Auto-replay when controls change
      setPlayCount((c) => c + 1);
    },
    [],
  );

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleIterate();
    }
  };

  const handleInputFocus = useCallback(() => {
    onInputFocus?.(true);
    setTimeout(() => {
      const card = cardRef.current;
      const scroller = card?.closest("[data-scroll-container]") as HTMLElement | null;
      if (!card || !scroller) return;

      const vv = window.visualViewport;
      const visibleHeight = vv?.height ?? window.innerHeight;
      const gap = 16;

      const cardRect = card.getBoundingClientRect();
      const scrollerRect = scroller.getBoundingClientRect();

      const cardBottomInScroller = cardRect.bottom - scrollerRect.top + scroller.scrollTop;
      const targetScrollTop = cardBottomInScroller - visibleHeight + gap;

      scroller.scrollTo({ top: Math.max(0, targetScrollTop), behavior: "smooth" });
    }, 350);
  }, [onInputFocus]);

  const handleInputBlur = useCallback(() => {
    onInputFocus?.(false);
  }, [onInputFocus]);

  const gradient = useMemo(
    () => pickGradient(data.prompt ?? "animation-designer"),
    [data.prompt],
  );

  // Get current control values (from overrides or from config defaults)
  const getControlValue = useCallback(
    (control: AnimationControl): number | boolean | string => {
      if (control.key in overrides) return overrides[control.key];
      return control.value;
    },
    [overrides],
  );

  return (
    <div
      ref={cardRef}
      className="overflow-hidden max-w-md animate-slide-up rounded-3xl rounded-bl-lg"
      style={{ background: gradient }}
    >
      {/* Scoped keyframes style */}
      {mergedConfig?.keyframes && (
        <style>{mergedConfig.keyframes}</style>
      )}

      {/* Animation Preview */}
      <div className="relative overflow-hidden" style={{ background: "#1e1e22" }}>
        {isEmpty ? (
          <div className="flex flex-col items-center justify-center min-h-[200px] gap-2">
            <Sparkles size={20} className="text-daydra-muted/30" />
            <span className="text-sm text-daydra-muted/40">Describe an animation to preview</span>
          </div>
        ) : (
          <div className="flex items-center justify-center p-4 min-h-[200px] relative">
            {mergedConfig && renderDemo(mergedConfig.demo, animStyle, playCount)}
          </div>
        )}

        {/* Replay button */}
        {!isEmpty && (
          <button
            onClick={handleReplay}
            className="absolute top-2 right-2 p-1.5 rounded-full bg-black/40 backdrop-blur-sm
              text-white/60 hover:text-white/90 hover:bg-black/60 transition-all"
            title="Replay"
          >
            <RotateCcw size={13} />
          </button>
        )}

        {/* Version navigation overlay */}
        {versions.length > 1 && (
          <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex items-center gap-1.5 bg-black/60 backdrop-blur-sm rounded-full px-2 py-1">
            <button
              onClick={() => canGoBack && setCurrentIndex(currentIndex - 1)}
              disabled={!canGoBack}
              className={cn(
                "p-0.5 rounded-full transition-opacity",
                canGoBack ? "text-white hover:bg-white/20" : "text-white/30 cursor-default",
              )}
            >
              <ChevronLeft size={14} />
            </button>
            <span className="text-2xs text-white/80 font-mono min-w-[3ch] text-center">
              {currentIndex + 1}/{versions.length}
            </span>
            <button
              onClick={() => canGoForward && setCurrentIndex(currentIndex + 1)}
              disabled={!canGoForward}
              className={cn(
                "p-0.5 rounded-full transition-opacity",
                canGoForward ? "text-white hover:bg-white/20" : "text-white/30 cursor-default",
              )}
            >
              <ChevronRight size={14} />
            </button>
          </div>
        )}
      </div>

      {/* Controls Panel */}
      {mergedConfig && mergedConfig.controls.length > 0 && (
        <div className="px-3 py-2.5 space-y-2 border-t border-white/[0.06]">
          {mergedConfig.controls.map((control) => {
            const val = getControlValue(control);
            switch (control.type) {
              case "slider":
                return (
                  <SliderControl
                    key={control.key}
                    control={control}
                    value={val as number}
                    onChange={(v) => handleControlChange(control.key, v)}
                  />
                );
              case "toggle":
                return (
                  <ToggleControl
                    key={control.key}
                    control={control}
                    value={val as boolean}
                    onChange={(v) => handleControlChange(control.key, v)}
                  />
                );
              case "select":
                return (
                  <SelectControl
                    key={control.key}
                    control={control}
                    value={val as string}
                    onChange={(v) => handleControlChange(control.key, v)}
                  />
                );
              default:
                return null;
            }
          })}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex items-center gap-2 px-3 py-2 border-t border-white/[0.06]">
        {!isEmpty && (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleCopy}
              className="gap-1.5"
            >
              {copied ? <Check size={13} /> : <Copy size={13} />}
              {copied ? "Copied" : "Copy CSS"}
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClear}
              className="gap-1.5"
            >
              <Trash2 size={13} />
              Clear
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleConfirm}
              className="gap-1.5"
              style={{
                background: "linear-gradient(135deg, rgba(232,164,74,0.2) 0%, rgba(232,111,74,0.15) 100%)",
                borderColor: "rgba(232,164,74,0.3)",
              }}
            >
              <Check size={13} />
              Confirm
            </Button>
          </>
        )}
        {data.prompt && (
          <span className="text-2xs text-daydra-muted truncate flex-1 text-right">
            {data.prompt}
          </span>
        )}
      </div>

      {/* Input */}
      <div className="flex items-center gap-2 px-3 pb-3 pt-1">
        <div className="flex-1 min-w-0 bg-black/30 rounded-2xl px-3 ring-1 ring-white/[0.04] transition-shadow duration-normal focus-within:shadow-[0_0_24px_rgba(134,87,241,0.18)]">
          <textarea
            ref={inputRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onBlur={handleInputBlur}
            placeholder={isEmpty ? "Describe an animation..." : "Describe changes..."}
            disabled={loading}
            rows={1}
            className="w-full resize-none bg-transparent py-[9px] text-sm leading-snug text-daydra-text placeholder:text-daydra-muted/50 focus:outline-none disabled:opacity-50"
            style={{ height: "36px", maxHeight: "80px", overflowY: "hidden" }}
            onInput={(e) => {
              const target = e.target as HTMLTextAreaElement;
              target.style.height = "36px";
              const h = Math.min(target.scrollHeight, 80);
              target.style.height = h + "px";
              target.style.overflowY = h >= 80 ? "auto" : "hidden";
            }}
          />
        </div>
        <button
          onMouseDown={(e) => { e.preventDefault(); handleIterate(); }}
          onTouchEnd={(e) => { e.preventDefault(); handleIterate(); }}
          disabled={!input.trim() || loading}
          className={cn(
            "flex items-center justify-center w-9 rounded-full transition-all duration-normal shrink-0",
            input.trim() && !loading
              ? "text-daydra-bg hover:brightness-110 scale-100"
              : "text-daydra-muted/40 scale-95 bg-transparent",
            "disabled:opacity-30 disabled:scale-95",
          )}
          style={{
            height: "36px",
            ...(input.trim() && !loading ? { background: "var(--brand-gradient)" } : {}),
          }}
        >
          {loading ? (
            <Loader2 size={16} className="animate-spin" />
          ) : (
            <ArrowUp size={16} strokeWidth={2.5} />
          )}
        </button>
      </div>
    </div>
  );
}
